import { Button, IconButton, Stack, Typography, alpha } from '@mui/material';
import {
  addDays,
  format,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  isSameDay,
  parseISO,
  startOfDay,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS, pl } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';
import DateSkeleton from './VisitCalendarHoursSkeleton';
import VisitCalendarNoAvailabilities from './VisitCalendarNoAvailabilities';

interface IProps {
  dates: Array<{ date: Date; available: boolean }>;
  onSelect: (date: Date) => void;
  disabled?: boolean;
  isLoading?: boolean;
  isQuickBooking?: boolean;
  selectedDate?: string;
  timeZone?: string;
  // Week navigation props
  startOfWeek?: Date;
  onWeekChange?: (startOfWeek: Date) => void;
  // Button props
  showButtons?: boolean;
  primaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  secondaryButtonProps?: any;
  numberOfSkeletonRows?: number;
  calendarColumns?: number;
  noDatesPlaceholder?: string;
}

interface IDateAvailability {
  date: Date;
  available: boolean;
}

const CalendarComponent: React.FC<IProps> = ({
  dates,
  onSelect,
  disabled,
  isLoading,
  isQuickBooking,
  selectedDate,
  timeZone,
  startOfWeek: externalStartOfWeek,
  onWeekChange,
  numberOfSkeletonRows = 5,
  calendarColumns,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'pl' ? pl : enUS;
  const getToday = (): Date =>
    timeZone
      ? parseISO(formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'))
      : startOfDay(new Date());
  const today = getToday();
  const [internalStartOfWeek, setInternalStartOfWeek] = useState<Date>(today);
  const { isTabletSm, isTabletLg } = useResponsiveBreakpoints();

  // Use external startOfWeek if provided, otherwise use internal state
  const startOfWeek = externalStartOfWeek || internalStartOfWeek;
  const [selectedHour, setSelectedHour] = useState<Date | null>(
    selectedDate ? new Date(selectedDate) : null,
  );

  const maxDate = addDays(today, 11);

  const isSameDayHourAndMinute = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate() &&
      date1.getHours() === date2.getHours() &&
      date1.getMinutes() === date2.getMinutes()
    );
  };

  const getDayName = (date: Date) => {
    if (timeZone ? isSameDay(date, today) : isToday(date)) {
      return t('visit.calendarToday');
    }

    if (timeZone ? isSameDay(date, addDays(today, 1)) : isTomorrow(date)) {
      return t('visit.calendarTomorrow');
    }
    return format(date, 'EEE', { locale: dateLocale }).replace('.', '');
  };

  useEffect(() => {
    if (selectedDate) {
      setSelectedHour(new Date(selectedDate));
    } else {
      setSelectedHour(null);
    }
  }, [selectedDate]);

  const moveWeek = (days: number) => {
    const updateWeek = (prevDate: Date): Date => {
      const newDate = addDays(prevDate, days);
      const today = getToday();
      if (isBefore(newDate, today)) {
        return today;
      }
      return newDate;
    };

    if (onWeekChange) {
      // Use external state management
      const newWeek = updateWeek(startOfWeek);
      onWeekChange(newWeek);
    } else {
      // Use internal state management
      setInternalStartOfWeek((prevDate) => updateWeek(prevDate));
    }
  };

  const daysLength = calendarColumns || (isTabletSm ? 2 : isTabletLg ? 3 : 4);

  const days = Array.from({ length: daysLength }).map((_, i) =>
    addDays(startOfWeek, i),
  );
  const dateAvailabilities: IDateAvailability[][] = days.map((day) =>
    dates
      .filter(({ date }) =>
        timeZone
          ? formatInTimeZone(date, timeZone, 'yyyy-MM-dd') ===
            format(day, 'yyyy-MM-dd')
          : isSameDay(date, day),
      )
      // .slice(0, showAllHours ? undefined : 5)
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex((t) => isSameDayHourAndMinute(t.date, item.date)),
      )
      .map(({ date, available }) => ({
        date,
        available,
      })),
  );
  const isDateSpanEmpty = dateAvailabilities.every((day) => day.length === 0);

  return (
    <>
      <Stack
        sx={{
          flex: 1,
          position: 'relative',
          height: '100%',
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          flex={1}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
        >
          <IconButton
            onClick={() => moveWeek(-daysLength)}
            disabled={
              isBefore(startOfWeek, today) ||
              startOfWeek.getTime() === today.getTime() ||
              disabled
            }
            size="small"
            sx={{
              bgcolor: alpha('#50CEFF', 0.16),
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'transparent' },
            }}
          >
            <Iconify icon="ic:round-chevron-left" width={40} />
          </IconButton>

          <Stack
            direction="column"
            gap={2}
            flex={1}
            sx={{
              mx: { md: 0, lg: 2 },
              width: 'calc(100% - 128px)',
              '@media (max-width:475px)': {
                width: 'calc(100% - 88px)',
              },
              '@media (max-width:360px)': {
                width: 'calc(100% - 128px)',
              },
              '@media (max-width:330px)': {
                width: 'calc(100% - 108px)',
              },
              '@media (max-width:300px)': {
                width: 'calc(100% - 78px)',
              },
              height: '100%',
            }}
          >
            <Stack flexDirection="row" justifyContent="space-between">
              {dateAvailabilities.map((dayAvailabilities, i) => (
                <Stack
                  sx={{
                    width: isQuickBooking ? '71px' : '83px',
                    '@media (max-width:475px)': {
                      width: '66px',
                    },
                  }}
                  key={i}
                >
                  <Stack>
                    <Typography
                      variant="body2"
                      sx={{ textTransform: 'capitalize', textAlign: 'center' }}
                    >
                      {getDayName(days[i])}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: 'capitalize', textAlign: 'center' }}
                    >
                      {timeZone
                        ? format(days[i], 'd LLL', { locale: dateLocale })
                        : formatInTimeZone(days[i], 'Europe/Warsaw', 'd LLL', {
                            locale: dateLocale,
                          })}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>

            {isDateSpanEmpty && !isLoading && <VisitCalendarNoAvailabilities />}

            {!isDateSpanEmpty && (
              <Scrollbar
                sx={{
                  flex: 1,
                  height: '100%',
                }}
              >
                <Stack
                  flexDirection="row"
                  flex={1}
                  justifyContent="space-between"
                  position="relative"
                >
                  {dateAvailabilities.map((dayAvailabilities, i) => (
                    <Stack
                      spacing={2}
                      key={i}
                      sx={{
                        width: isQuickBooking ? '71px' : '83px',
                        '@media (max-width:475px)': {
                          width: '66px',
                        },
                        minHeight: '40px', // Ensure minimum height for empty columns
                      }}
                    >
                      {!isLoading ? (
                        <Stack gap={1}>
                          {dayAvailabilities.length > 0 ? (
                            dayAvailabilities.map(({ date, available }, i) => {
                              const isSelected =
                                selectedHour &&
                                isSameDayHourAndMinute(selectedHour, date);

                              return (
                                <Button
                                  key={i}
                                  variant="contained"
                                  data-testid="visit-calendar-slot"
                                  data-available={available ? 'true' : 'false'}
                                  onClick={() => {
                                    if (available) {
                                      // Check if the same date (hours and minutes) is already selected
                                      if (
                                        selectedHour &&
                                        isSameDayHourAndMinute(
                                          selectedHour,
                                          date,
                                        )
                                      ) {
                                        // Deselect if clicking the same time slot
                                        setSelectedHour(null);
                                        onSelect(date); // Still call onSelect to let parent handle deselection
                                      } else {
                                        // Select new time slot
                                        setSelectedHour(date);
                                        onSelect(date);
                                      }
                                    }
                                  }}
                                  disabled={!available || disabled}
                                  size="small"
                                  sx={{
                                    width: isQuickBooking ? '71px' : '83px',
                                    '@media (max-width:475px)': {
                                      width: '66px',
                                    },
                                    bgcolor: isSelected
                                      ? 'primary.main'
                                      : alpha('#50CEFF', 0.16),
                                    color: isSelected
                                      ? 'text.primary'
                                      : 'primary.dark',
                                    '&:hover': {
                                      color: 'text.primary',
                                    },
                                    '&.Mui-disabled': {
                                      bgcolor: alpha('#9AA5A9', 0.08),
                                      textDecoration:
                                        !available && !disabled
                                          ? 'line-through'
                                          : 'none',
                                    },
                                  }}
                                >
                                  <span>
                                    {formatInTimeZone(
                                      date,
                                      timeZone || 'Europe/Warsaw',
                                      'H:mm',
                                    )}
                                  </span>
                                </Button>
                              );
                            })
                          ) : (
                            // Render empty space to maintain column structure
                            <Stack sx={{ minHeight: '40px' }} />
                          )}
                        </Stack>
                      ) : (
                        <DateSkeleton count={numberOfSkeletonRows} />
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Scrollbar>
            )}
          </Stack>
          <IconButton
            size="small"
            data-testid="visit-calendar-next"
            onClick={() => moveWeek(daysLength)}
            disabled={isAfter(startOfWeek, maxDate) || disabled}
            sx={{
              bgcolor: alpha('#50CEFF', 0.16),
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'transparent' },
            }}
          >
            <Iconify icon="ic:round-chevron-right" width={40} />
          </IconButton>
        </Stack>
      </Stack>
      {/* TODO: show more hours button */}
    </>
  );
};

export default CalendarComponent;
